import { useCallback, useEffect, useRef } from 'react'
import { pureAxios } from 'lib/axios'

const TIME_CONSTANTS = {
  FIVE_MINUTES: 5 * 60 * 1000,
  FIVE_SECONDS: 5 * 1000,
}

const DEFAULT_CONFIG = {
  PING_THROTTLE: TIME_CONSTANTS.FIVE_MINUTES,
  ACTIVITY_DEBOUNCE: TIME_CONSTANTS.FIVE_SECONDS,
}

const useSessionKeepAlive = ({
  pingThrottle = DEFAULT_CONFIG.PING_THROTTLE,
  activityDebounce = DEFAULT_CONFIG.ACTIVITY_DEBOUNCE,
  onPingSuccess = null
} = {}) => {
  const lastActivityRef = useRef(Date.now())
  const lastPingRef = useRef(Date.now())
  const activityDebounceRef = useRef(null)

  const pingServer = useCallback(async () => {
    try {
      const response = await pureAxios.get('/check_sign_in', { withCredentials: true })

      lastPingRef.current = Date.now()

      if (onPingSuccess) {
        onPingSuccess(response)
      }

      return true
    } catch (error) {
      console.error('Failed to refresh session:', error)
      return false
    }
  }, [onPingSuccess])

  const clearAllTimers = useCallback(() => {
    if (activityDebounceRef.current) clearTimeout(activityDebounceRef.current)
  }, [])

  const resetSessionTimer = useCallback(async () => {
    const now = Date.now()
    lastActivityRef.current = now

    const timeSinceLastPing = now - lastPingRef.current

    if (timeSinceLastPing >= pingThrottle) {
      await pingServer()
    }
  }, [pingServer, pingThrottle])

  useEffect(() => {
    const activityEvents = ['mousedown', 'keypress', 'scroll']

    const handleActivity = () => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current

      if (timeSinceLastActivity > activityDebounce) {
        if (activityDebounceRef.current) {
          clearTimeout(activityDebounceRef.current)
        }

        activityDebounceRef.current = setTimeout(() => {
          resetSessionTimer()
        }, 100)
      }
    }

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, true)
    })

    resetSessionTimer()

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity, true)
        clearAllTimers()
      })
    }
  }, [resetSessionTimer, clearAllTimers, activityDebounce])

  return {
    pingServer,
    resetSessionTimer,
  }
}

export default useSessionKeepAlive
